import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { refreshExpiredFunc } from '../store/slices/refreshExpiredReducer';
import { globalVal } from '../utils/global-values';
import { errorMsgs } from '../utils/error-msgs';
import { enqueueSnackbar } from 'notistack';
import { decrypt } from '../utils/decrypt';

const ERROR_TOAST_WINDOW_MS = 2000;
let lastErrorToastKey = '';
let lastErrorToastAt = 0;

const enqueueErrorOnce = (message: string, key?: string) => {
  const now = Date.now();
  const dedupeKey = key ?? message;

  if (dedupeKey === lastErrorToastKey && now - lastErrorToastAt < ERROR_TOAST_WINDOW_MS) {
    return;
  }

  lastErrorToastKey = dedupeKey;
  lastErrorToastAt = now;
  enqueueSnackbar(message, { variant: 'error' });
};

export const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_ADDRESS || 'https://corporatetestapi.ahlpay.com.tr/api/',
  prepareHeaders: (headers) => {
    let token = localStorage.getItem('accessToken');
    if (!token) {
      token = localStorage.getItem('verifyToken');
    }
    if (!token) {
      token = localStorage.getItem('forgotPasswordToken');
    }
    if (token && localStorage.getItem('accessToken')) {
      localStorage.removeItem('verifyToken');
    }
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const pathname = window.location.pathname;
  const isLoginPage = window.location.href.includes('/login') || pathname === '/login';
  // Şifre unuttum / sıfırlama / doğrulama sayfalarında oturum yok; isteği bloklama
  const isAuthFlowPage = isLoginPage ||
    pathname.includes('/authentication/forget-pass') ||
    pathname.includes('/reset-password') ||
    pathname.includes('/authentication/verify');

  if (globalVal.isLoggedOut && !isAuthFlowPage) {
    // Eğer kullanıcı çıkış yapmışsa ve auth akış sayfasında değilse, istekleri durduruyoruz.
    return { error: { status: 401, data: { message: 'Oturum kapalı, tekrar giriş yapın.' } } };
  }

  try {
    let result: any = await baseQuery(args, api, extraOptions);
    api.dispatch(refreshExpiredFunc())
    if (result.data && result.data.data?.token) {
      let getSession = localStorage.getItem('accessToken');

      if (getSession)
        localStorage.setItem('accessToken', result.data.data.token);
      else
        localStorage.setItem('verifyToken', result.data.data.token);
    }
    if (result.error) {
      let handled = false;

      // Sadece bir kez hata mesajı gösterilmesi için, önce açık kart numarası kontrolü, sonra şifreli için decrypt
      // Eğer data dizisinde açık hesap/kart numarası varsa, doğrudan göster; eğer decrypt başarılı dönerse onu kullan
      const cardNumbers = (Array.isArray(result.error?.data?.data)) ? result.error.data.data : null;
      if (
        cardNumbers &&
        cardNumbers.length > 0 &&
        result.error?.data?.message
      ) {
        // Her bir kartı decrypt etmeyi dene, başarılıysa decrypt edilmiş halini, değilse orijinalini kullan
        const displayCards = cardNumbers.map((item: string) => {
          try {
            const decrypted = decrypt(item);
            // Decrypt başarılı ve farklı ise onu kullan
            return decrypted !== "" ? decrypted : item;
          } catch {
            return item;
          }
        });
        const combinedMessage = `${displayCards.join(', ')} - ${result.error.data.message}`;
        enqueueErrorOnce(combinedMessage);
        result.error.data.message = combinedMessage;
        handled = true;
      }

      if (!handled && result.error?.data?.message && result.error?.status !== 401) {
        if (result.error?.status === 404) {
          // 404 kayıt yok demek bunun için hata mesaji dönme epin için yapıldı bu condition.
          return;
        }
        if (Array.isArray(result.error?.data?.message)) {
          const msgs = result.error.data.message.map((item: any) =>
            typeof item === 'string' ? item : (item?.message ?? JSON.stringify(item))
          );
          const msgKey = result.error?.status === 0 ? 'network-error' : msgs.join('\n');
          enqueueErrorOnce(msgs.join('\n'), msgKey);
        } else if (result.error?.data?.message) {
          const msgText = typeof result.error.data.message === 'string'
            ? result.error.data.message
            : JSON.stringify(result.error.data.message);
          const msgKey = result.error?.status === 0 ? 'network-error' : msgText;
          enqueueErrorOnce(msgText, msgKey);
        }
      } else if (!handled) {

        if (!globalVal.isLoggedOut && !window.location.href.includes('/authentication/forget-pass')) {
          const fallbackMsg = errorMsgs(result.error?.status, api);
          const fallbackKey =
            result.error?.status === 401
              ? 'auth-401'
              : result.error?.status === 0
                ? 'network-error'
                : fallbackMsg;
          enqueueErrorOnce(fallbackMsg, fallbackKey);
        }
        if (result.error?.status === 401) {
          globalVal.isLoggedOut = true; // Çıkış yapılmış durumu işaretle
        }
        // 500 hatası alındığında bir önceki sayfaya dönme
        /* if (result.error?.status === 500) {
          window.history.back();
        } */
      }
    }
    return result;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      enqueueErrorOnce('İstek zaman aşımına uğradı', 'network-error');
    } else {
      enqueueErrorOnce('Beklenmeyen bir hata oluştu', 'network-error');
    }
  } finally {
  }
};

